import * as React from 'react';
import Box from '@mui/joy/Box';
import Table from '@mui/joy/Table';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import { parseUtcDate } from '../../../common/dateTime';
import { formatRubles } from '../../../common/money';
import { AdminGeneralInfoResult } from '../../../models/adminGeneral';

export type DailyProfit = { days: string[]; values: number[] };

export type DailyProfitTableProps = {
    data: AdminGeneralInfoResult | null;
    fromDate: string;
    toDate: string;
};

export const DailyProfitTable: React.FC<DailyProfitTableProps> = React.memo(({ data, fromDate, toDate }) => {
    const avgPerDay = data?.profitInDaySum ?? 0;

    const dailyProfit = React.useMemo(() => {
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);

        // Group by LOCAL calendar day to match displayed labels and avoid UTC drift
        const dayKeyLocal = (d: Date) => `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
        const map = new Map<string, number>();

        if (data?.totalProfit) {
            for (const p of data.totalProfit) {
                const t = parseUtcDate(p.bucketUtc);
                if (t < start || t > end) continue;
                const key = dayKeyLocal(t);
                map.set(key, (map.get(key) ?? 0) + (p.value ?? 0));
            }
        }

        const days: string[] = [];
        const values: number[] = [];
        // Iterate by LOCAL dates inclusively
        const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        while (cur <= endLocal) {
            const key = dayKeyLocal(cur);
            days.push(cur.toLocaleDateString('ru-RU'));
            values.push(map.get(key) ?? 0);
            cur.setDate(cur.getDate() + 1);
        }

        return { days, values };
    }, [data?.totalProfit, fromDate, toDate]);

    const tableScrollRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        const el = tableScrollRef.current;
        if (el) {
            el.scrollLeft = el.scrollWidth;
        }
    }, [dailyProfit.days, dailyProfit.values]);

    return (
        <>
            {/* Desktop/tablet: days as columns */}
            <Box sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }} ref={tableScrollRef}>
                <Table borderAxis="bothBetween" size="sm" stickyHeader sx={{ tableLayout: 'auto', minWidth: 600 }}>
                    <thead>
                        <tr>
                            {dailyProfit.days.map((d, idx) => (
                                <th key={idx} style={{ whiteSpace: 'nowrap' }}>{d}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {dailyProfit.values.map((v, idx) => {
                                const isLess = v < avgPerDay;
                                const isGreater = v > avgPerDay;
                                const color = isLess ? 'danger.600' : (isGreater ? 'success.600' : undefined);
                                const diff = Math.abs(v - avgPerDay);
                                const title = isLess
                                    ? `Меньше чем среднее за выбранный период (${formatRubles(avgPerDay)}) на ${formatRubles(diff)}`
                                    : (isGreater ? `Больше чем среднее за выбранный период (${formatRubles(avgPerDay)}) на ${formatRubles(diff)}` : undefined);
                                const content = (
                                    <Typography level="body-md" sx={{ color }}>{formatRubles(v)}</Typography>
                                );
                                return (
                                    <td key={idx} style={{ whiteSpace: 'nowrap' }}>
                                        {title ? (
                                            <Tooltip title={title} disablePortal={false}>
                                                {content}
                                            </Tooltip>
                                        ) : content}
                                    </td>
                                );
                            })}
                        </tr>
                    </tbody>
                </Table>
            </Box>

            {/* Mobile: two columns (Дата, Сумма), days as rows */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Table borderAxis="bothBetween" size="sm" stickyHeader>
                    <thead>
                        <tr>
                            <th style={{ whiteSpace: 'nowrap' }}>Дата</th>
                            <th style={{ whiteSpace: 'nowrap' }}>Сумма</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dailyProfit.days.map((day, idx) => {
                            const v = dailyProfit.values[idx] ?? 0;
                            const isLess = v < avgPerDay;
                            const isGreater = v > avgPerDay;
                            const color = isLess ? 'danger.600' : (isGreater ? 'success.600' : undefined);
                            const diff = Math.abs(v - avgPerDay);
                            const title = isLess
                                ? `Меньше чем среднее за выбранный период (${formatRubles(avgPerDay)}) на ${formatRubles(diff)}`
                                : (isGreater ? `Больше чем среднее за выбранный период (${formatRubles(avgPerDay)}) на ${formatRubles(diff)}` : undefined);
                            const content = (
                                <Typography level="body-md" sx={{ color }}>{formatRubles(v)}</Typography>
                            );
                            return (
                                <tr key={idx}>
                                    <td style={{ whiteSpace: 'nowrap' }}>{day}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {title ? (
                                            <Tooltip title={title} disablePortal={false}>
                                                {content}
                                            </Tooltip>
                                        ) : content}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Box>
        </>
    );
});

DailyProfitTable.displayName = 'DailyProfitTable';


